// One shared unsaved-changes guard for the form builder and its header.
//
// The builder reports whether its page is dirty (setDirtyState) and how to
// save it (registerSave) or throw the local backup away (registerDiscard).
// Anything that leaves the page goes through guardedNavigate, which asks
// "Save and continue / Leave without saving / Stay" while dirty.
//
// Also covered while dirty:
//  - refresh / close tab: the browser's own "Leave site?" prompt (beforeunload)
//  - Back / Forward: a duplicate history entry of the current URL is pushed, so
//    pressing Back lands on the same URL (the builder stays mounted with the
//    edits) and the same modal is shown. react-router 6.2 has no blocker API.
//
// Outside a provider (Preview, Share, Integrate, Results) useUnsavedChanges()
// falls back to plain navigation.
//
// Live forms: the header reports whether the form is published and how many
// responses it has (setLiveInfo). Saving a published form updates what
// everyone sees on the link, so Save Form and "Update live form" ask first
// (confirmLiveUpdate), and the leave-page question says so too.

import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import "./UnsavedChangesContext.css";

const UnsavedChangesContext = createContext(null);

const noop = () => { };

export function UnsavedChangesProvider({ children }) {
    const navigate = useNavigate();
    const [isDirty, setIsDirty] = useState(false);
    const [savedOnce, setSavedOnce] = useState(false);

    const dirtyRef = useRef(false);
    dirtyRef.current = isDirty;
    const saveRef = useRef(null);
    const discardRef = useRef(null);
    const modalOpenRef = useRef(false);
    const sentinelRef = useRef(false);
    const leavingRef = useRef(false);
    const liveRef = useRef({ published: false, responses: 0 });

    const setDirtyState = useCallback((dirty) => {
        dirtyRef.current = !!dirty;
        setIsDirty(!!dirty);
    }, []);
    const registerSave = useCallback((fn) => { saveRef.current = fn; }, []);
    const registerDiscard = useCallback((fn) => { discardRef.current = fn; }, []);
    const notifySaved = useCallback(() => setSavedOnce(true), []);
    const setLiveInfo = useCallback((info) => { liveRef.current = { ...liveRef.current, ...info }; }, []);

    // Run the builder's save (the same one "Save and continue" uses).
    // true = saved; the save shows its own error otherwise.
    const saveNow = useCallback(async () => {
        const saved = saveRef.current ? await saveRef.current() : false;
        return saved === true;
    }, []);

    // Before saving a published form: its link shows the new version at once.
    // Resolves true to go ahead. Unpublished forms never ask.
    const confirmLiveUpdate = useCallback(async () => {
        const { published, responses } = liveRef.current;
        if (!published) return true;
        const answered = responses > 0
            ? ` ${responses} ${responses === 1 ? "person has" : "people have"} already responded; their answers are kept as they are.`
            : "";
        const result = await Swal.fire({
            title: "This form is live",
            text: `Saving will update it for everyone who opens the link.${answered}`,
            icon: "question",
            showCancelButton: true,
            confirmButtonText: "Save and update live form",
            cancelButtonText: "Cancel",
            focusConfirm: true,
        });
        return result.isConfirmed;
    }, []);

    // "save" | "leave" | "stay"
    const askUser = useCallback(async ({ preview } = {}) => {
        if (modalOpenRef.current) return "stay";
        modalOpenRef.current = true;
        try {
            const result = await Swal.fire({
                title: "You have unsaved changes",
                text: liveRef.current.published
                    ? "Save them before leaving this page? This form is live, so saving also updates it for everyone who opens the link."
                    : "Save them before leaving this page?",
                icon: "warning",
                showDenyButton: true,
                showCancelButton: true,
                confirmButtonText: preview ? "Save and preview" : "Save and continue",
                denyButtonText: "Leave without saving",
                cancelButtonText: "Stay",
                focusConfirm: true,
                customClass: {
                    actions: "unsaved-guard-actions",
                    denyButton: "unsaved-guard-leave",
                },
            });
            if (result.isConfirmed) return "save";
            if (result.isDenied) return "leave";
            return "stay";
        } finally {
            modalOpenRef.current = false;
        }
    }, []);

    // Ask (only while dirty) and act on the answer. true = OK to leave.
    // "Save" leaves only when the save succeeds; on failure the save shows its
    // own error and the edits stay on the page.
    const confirmLeave = useCallback(async (options = {}) => {
        if (!dirtyRef.current) return true;
        const choice = await askUser(options);
        if (choice === "save") {
            const saved = saveRef.current ? await saveRef.current() : false;
            if (saved !== true) return false;
        } else if (choice === "leave") {
            if (discardRef.current) discardRef.current();
        } else {
            return false;
        }
        dirtyRef.current = false;
        setIsDirty(false);
        return true;
    }, [askUser]);

    const guardedNavigate = useCallback(async (to, options = {}) => {
        const ok = await confirmLeave(options);
        if (ok) navigate(to, options.replace ? { replace: true } : undefined);
        return ok;
    }, [confirmLeave, navigate]);

    // Refresh / close tab / typed URL: the browser's own prompt.
    useEffect(() => {
        if (!isDirty) return undefined;
        const onBeforeUnload = (event) => {
            event.preventDefault();
            event.returnValue = "";
            return "";
        };
        window.addEventListener("beforeunload", onBeforeUnload);
        return () => window.removeEventListener("beforeunload", onBeforeUnload);
    }, [isDirty]);

    // A new URL (another page tab, another form) has no duplicate entry yet.
    const location = useLocation();
    const urlKey = `${location.pathname}${location.search}`;
    useEffect(() => {
        sentinelRef.current = false;
    }, [urlKey]);

    // Back / Forward while dirty (see the note at the top of this file).
    useEffect(() => {
        if (!isDirty) return undefined;
        if (!sentinelRef.current) {
            window.history.pushState(window.history.state, "", window.location.href);
            sentinelRef.current = true;
        }
        const onPopState = async () => {
            if (leavingRef.current) return;
            // Back landed on the duplicate entry: same URL, builder still here.
            sentinelRef.current = false;
            const ok = await confirmLeave();
            if (ok) {
                leavingRef.current = true;
                window.history.back();
                setTimeout(() => { leavingRef.current = false; }, 1000);
            } else {
                window.history.pushState(window.history.state, "", window.location.href);
                sentinelRef.current = true;
            }
        };
        window.addEventListener("popstate", onPopState);
        return () => window.removeEventListener("popstate", onPopState);
    }, [isDirty, confirmLeave, urlKey]);

    const value = useMemo(() => ({
        isDirty,
        saveStatus: isDirty ? "unsaved" : (savedOnce ? "saved" : null),
        setDirtyState,
        registerSave,
        registerDiscard,
        notifySaved,
        setLiveInfo,
        saveNow,
        confirmLiveUpdate,
        confirmLeave,
        guardedNavigate,
    }), [isDirty, savedOnce, setDirtyState, registerSave, registerDiscard, notifySaved, setLiveInfo, saveNow, confirmLiveUpdate, confirmLeave, guardedNavigate]);

    return <UnsavedChangesContext.Provider value={value}>{children}</UnsavedChangesContext.Provider>;
}

export function useUnsavedChanges() {
    const context = useContext(UnsavedChangesContext);
    const navigate = useNavigate();
    const fallback = useMemo(() => ({
        isDirty: false,
        saveStatus: null,
        setDirtyState: noop,
        registerSave: noop,
        registerDiscard: noop,
        notifySaved: noop,
        setLiveInfo: noop,
        saveNow: async () => false,
        confirmLiveUpdate: async () => true,
        confirmLeave: async () => true,
        guardedNavigate: async (to, options = {}) => {
            navigate(to, options.replace ? { replace: true } : undefined);
            return true;
        },
    }), [navigate]);
    return context || fallback;
}
